import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { templatesAPI, submissionsAPI, vehiclesAPI, driversAPI } from '../services/api';
import { 
  Database, 
  Download, 
  Loader2,
  FileSpreadsheet,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  RotateCcw,
  Search,
  Pencil,
  Trash2,
  ArchiveRestore,
  Server,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  draft: { color: 'bg-slate-100 text-slate-700', icon: Clock, label: 'Draft' },
  submitted: { color: 'bg-amber-100 text-amber-800', icon: Clock, label: 'Submitted' },
  approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
  rejected: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
  returned: { color: 'bg-orange-100 text-orange-800', icon: RotateCcw, label: 'Returned' },
  system_record: { color: 'bg-blue-100 text-blue-800', icon: Server, label: 'System Record' },
};

export const FormDataPage = () => {
  const { templateCode } = useParams();
  const navigate = useNavigate();
  
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(templateCode || '');
  const [templateData, setTemplateData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  
  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState({ open: false, record: null });
  const [deleting, setDeleting] = useState(false);

  // Fetch templates list
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const response = await templatesAPI.list();
        setTemplates(response.data || []);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      }
    };
    fetchTemplates();
  }, []);

  // Fetch template data when template changes
  useEffect(() => {
    if (selectedTemplate) {
      fetchTemplateData();
    }
  }, [selectedTemplate, statusFilter, dateFrom, dateTo, showDeleted]);

  const fetchTemplateData = async () => {
    if (!selectedTemplate) return;
    
    setLoading(true);
    try {
      const params = { include_deleted: showDeleted };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await templatesAPI.getData(selectedTemplate, params);
      setTemplateData(response.data);
    } catch (error) {
      console.error('Failed to fetch template data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateChange = (code) => {
    setSelectedTemplate(code);
    navigate(`/form-data/${code}`, { replace: true });
  };

  const handleExport = async () => {
    if (!selectedTemplate) return;
    
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (dateFrom) params.append('date_from', dateFrom);
      if (dateTo) params.append('date_to', dateTo);
      
      const token = localStorage.getItem('rtms_token');
      const response = await fetch(`${API_URL}/export/template-data/${selectedTemplate}?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${selectedTemplate}_Data.xlsx`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.record) return;
    
    setDeleting(true);
    try {
      const { record } = deleteDialog;
      
      if (record.record_type === 'vehicle') {
        await vehiclesAPI.delete(record.id);
      } else if (record.record_type === 'driver') {
        await driversAPI.delete(record.id);
      } else {
        await submissionsAPI.delete(record.id);
      }
      
      toast.success('Record deleted successfully');
      setDeleteDialog({ open: false, record: null });
      fetchTemplateData();
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete record');
    } finally {
      setDeleting(false);
    }
  };

  const handleRestore = async (record) => {
    try {
      if (record.record_type === 'vehicle') {
        await vehiclesAPI.restore(record.id);
      } else if (record.record_type === 'driver') {
        await driversAPI.restore(record.id);
      } else {
        await submissionsAPI.restore(record.id);
      }
      
      toast.success('Record restored successfully');
      fetchTemplateData();
    } catch (error) {
      console.error('Restore failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to restore record');
    }
  };

  const handleEdit = (record) => {
    if (record.record_type === 'vehicle') {
      navigate(`/vehicles?edit=${record.id}`);
    } else if (record.record_type === 'driver') {
      navigate(`/drivers?edit=${record.id}`);
    } else {
      navigate(`/submissions/${record.id}?edit=true`);
    }
  };

  // Get display fields from template (exclude line_items)
  const getDisplayFields = () => {
    if (!templateData?.fields) return [];
    return templateData.fields
      .filter(f => f.field_type !== 'line_items')
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .slice(0, 5); // Show first 5 fields
  };

  // Filter submissions by search
  const filteredSubmissions = templateData?.submissions?.filter(sub => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const formDataStr = JSON.stringify(sub.form_data || {}).toLowerCase();
    return (
      sub.submission_number?.toLowerCase().includes(query) ||
      sub.vehicle?.registration_number?.toLowerCase().includes(query) ||
      `${sub.driver?.user?.first_name || ''} ${sub.driver?.user?.last_name || ''}`.toLowerCase().includes(query) ||
      formDataStr.includes(query)
    );
  }) || [];

  // Stats
  const stats = {
    total: templateData?.total_count || 0,
    systemRecords: templateData?.direct_records_count || 0,
    submissions: templateData?.submission_count || 0,
    approved: templateData?.submissions?.filter(s => s.status === 'approved').length || 0,
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="form-data-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Barlow Condensed' }}
          >
            FORM DATA EXPLORER
          </h1>
          <p className="text-slate-500 mt-1">
            View, edit, and export all captured form data
          </p>
        </div>
        {selectedTemplate && templateData && (
          <Button 
            onClick={handleExport}
            disabled={exporting || !templateData?.submissions?.length}
            className="gap-2"
            data-testid="export-data-btn"
          >
            {exporting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Exporting...
              </>
            ) : (
              <>
                <Download className="w-4 h-4" />
                Export to Excel
              </>
            )}
          </Button>
        )}
      </div>

      {/* Template Selector & Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
            <div className="md:col-span-2 space-y-2">
              <Label>Select Template</Label>
              <Select value={selectedTemplate} onValueChange={handleTemplateChange}>
                <SelectTrigger data-testid="template-selector">
                  <SelectValue placeholder="Choose a form template..." />
                </SelectTrigger>
                <SelectContent>
                  {templates.map(t => (
                    <SelectItem key={t.code} value={t.code}>
                      {t.code} - {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger data-testid="status-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="submitted">Submitted</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="returned">Returned</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>From Date</Label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                data-testid="date-from-filter"
              />
            </div>
            
            <div className="space-y-2">
              <Label>To Date</Label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                data-testid="date-to-filter"
              />
            </div>

            <div className="space-y-2 flex items-end">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="show-deleted" 
                  checked={showDeleted}
                  onCheckedChange={setShowDeleted}
                />
                <Label htmlFor="show-deleted" className="text-sm cursor-pointer">
                  Show deleted
                </Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center min-h-[300px]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      )}

      {/* No Template Selected */}
      {!selectedTemplate && !loading && (
        <div className="text-center py-16">
          <Database className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700">Select a Template</h3>
          <p className="text-slate-500 mt-2 max-w-md mx-auto">
            Choose a form template from the dropdown above to view all captured data
          </p>
        </div>
      )}

      {/* Template Data View */}
      {selectedTemplate && templateData && !loading && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
                  <FileSpreadsheet className="w-5 h-5 text-slate-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-slate-900 font-mono">{stats.total}</p>
                  <p className="text-sm text-slate-500">Total Records</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                  <Server className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600 font-mono">{stats.systemRecords}</p>
                  <p className="text-sm text-slate-500">System Records</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-sm flex items-center justify-center">
                  <FileText className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-600 font-mono">{stats.submissions}</p>
                  <p className="text-sm text-slate-500">Form Submissions</p>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-sm flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600 font-mono">{stats.approved}</p>
                  <p className="text-sm text-slate-500">Approved</p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              placeholder="Search by submission #, vehicle, driver, or any field..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-11"
              data-testid="data-search"
            />
          </div>

          {/* Data Table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                {templateData.template?.name} 
                <Badge variant="outline">{filteredSubmissions.length} Records</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]">Type</TableHead>
                      <TableHead className="w-[140px]">Reference</TableHead>
                      <TableHead className="w-[100px]">Status</TableHead>
                      <TableHead className="w-[100px]">Date</TableHead>
                      {getDisplayFields().map(field => (
                        <TableHead key={field.name} className="min-w-[120px]">
                          {field.label}
                        </TableHead>
                      ))}
                      <TableHead className="w-[140px] text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredSubmissions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5 + getDisplayFields().length} className="text-center py-8 text-slate-500">
                          No records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredSubmissions.map((sub) => {
                        const statusConfig = STATUS_CONFIG[sub.status] || STATUS_CONFIG.draft;
                        const StatusIcon = statusConfig.icon;
                        const isDeleted = sub.is_deleted;
                        const isSystemRecord = sub.record_type === 'vehicle' || sub.record_type === 'driver';
                        
                        return (
                          <TableRow 
                            key={sub.id} 
                            data-testid={`data-row-${sub.submission_number}`}
                            className={isDeleted ? 'opacity-50 bg-slate-50' : ''}
                          >
                            <TableCell>
                              {isSystemRecord ? (
                                <Server className="w-4 h-4 text-blue-500" title="System Record" />
                              ) : (
                                <FileText className="w-4 h-4 text-amber-500" title="Form Submission" />
                              )}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {sub.submission_number}
                              {isDeleted && (
                                <Badge variant="outline" className="ml-2 text-red-500 border-red-200">
                                  Deleted
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={`${statusConfig.color} gap-1`}>
                                <StatusIcon className="w-3 h-3" />
                                {statusConfig.label}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {sub.created_at?.slice(0, 10)}
                            </TableCell>
                            {getDisplayFields().map(field => {
                              let value = sub.form_data?.[field.name];
                              if (typeof value === 'boolean') value = value ? 'Yes' : 'No';
                              if (value === null || value === undefined) value = '-';
                              // Format dates
                              if (field.field_type === 'date' && value && value !== '-') {
                                value = value.slice(0, 10);
                              }
                              return (
                                <TableCell key={field.name} className="text-sm max-w-[200px] truncate">
                                  {String(value)}
                                </TableCell>
                              );
                            })}
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                {!isDeleted ? (
                                  <>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        if (isSystemRecord) {
                                          if (sub.record_type === 'vehicle') {
                                            navigate(`/vehicles`);
                                          } else {
                                            navigate(`/drivers`);
                                          }
                                        } else {
                                          navigate(`/submissions/${sub.id}`);
                                        }
                                      }}
                                      title="View"
                                      data-testid={`view-${sub.submission_number}`}
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleEdit(sub)}
                                      title="Edit"
                                      data-testid={`edit-${sub.submission_number}`}
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setDeleteDialog({ open: true, record: sub })}
                                      title="Delete"
                                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                      data-testid={`delete-${sub.submission_number}`}
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </>
                                ) : (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRestore(sub)}
                                    title="Restore"
                                    className="text-green-500 hover:text-green-700 hover:bg-green-50"
                                    data-testid={`restore-${sub.submission_number}`}
                                  >
                                    <ArchiveRestore className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => !deleting && setDeleteDialog({ open, record: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Confirm Delete
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{deleteDialog.record?.submission_number}</strong>?
              <br /><br />
              This record will be marked as inactive and can be restored later.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              disabled={deleting}
              className="bg-red-500 hover:bg-red-600"
            >
              {deleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default FormDataPage;
